import * as Sentry from '@sentry/react';
import {
  Activity,
  ActivityStats,
  ActivityZone,
  AppLogin,
  Athlete,
  Response,
  Stats,
  StravaLogin,
  User,
  Zones,
} from '../types';

export const isProduction = import.meta.env.PROD;
export const netlifyUrl = 'https://strava-dashboard.netlify.app/';
export const baseUrl = isProduction ? netlifyUrl : 'http://localhost:5000/';
// secrets
const {
  VITE_CLIENT_ID: client_id,
  VITE_CLIENT_SECRET: client_secret,
  VITE_REFRESH_TOKEN: refreshToken,
} = import.meta.env;

export const getStats = async () => {
  const url = baseUrl + 'stats';
  const stats: Promise<Stats[]> = await fetch(url, {
    method: 'GET',
    mode: 'cors',
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error(error.message);
    });

  return stats;
};

export const postLogin = async (loginData: AppLogin) => {
  const url = baseUrl + 'login';
  const body = JSON.stringify(loginData);
  const response: Promise<User> = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error(error.message);
    });

  return response;
};

export const postSravaLogin = async (loginData: StravaLogin) => {
  const url = baseUrl + 'strava-login';
  const body = JSON.stringify(loginData);
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
  });

  return response;
};

export const headScrap = async (withAthletes: boolean) => {
  const queryParam = `?withAthletes=${withAthletes}`;
  const url = baseUrl + 'scrap' + queryParam;
  // avoid error CORS
  await fetch(url, { method: 'HEAD', mode: 'cors' }).catch((error) =>
    console.error(error.message)
  );
};

export const getAthletes = async () => {
  const url = baseUrl + 'athletes';
  const athletes: Promise<Athlete[]> = await fetch(url, {
    method: 'GET',
    mode: 'cors',
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error(error.message);
    });

  return athletes;
};

// **************************************************************
// Strava API v3
// **************************************************************

const perPage = 200; // {Integer} Number of items per page. Defaults to 30. Limit to 200

/**
 * - [ after -> -> before ] -
 * @param access_token
 * @param page
 * @returns
 */
export const getActivityList = async (
  access_token: string,
  after: number,
  before: number,
  page: number
) => {
  const callActivitiesUrl = (access_token: string, page = 1) =>
    `https://www.strava.com/api/v3/athlete/activities?before=${before}&after=${after}&page=${page}&per_page=${perPage}&access_token=${access_token}`;

  const response: Promise<Activity[]> = await fetch(
    callActivitiesUrl(access_token, page)
  )
    .then((res) => res.json())
    .catch((error) => console.error(error.message));

  return response;
};

// used the first time a user requests an access with the approval_prompt
export const postCallRefreshUrl = async () => {
  const callRefreshUrl = `https://www.strava.com/oauth/token?client_id=${client_id}&client_secret=${client_secret}&refresh_token=${refreshToken}&grant_type=refresh_token`;
  const response: Promise<Response> = await fetch(callRefreshUrl, {
    method: 'POST',
  })
    .then((res) => res.json())
    .catch((error) => {
      Sentry.captureException(error);
      console.error(error.message);
    });

  return response;
};

// access_token valid 6 hours long, used to get a new access_token
// the Refresh Token does not expire
export const postCallGetAccesToken = async (authorization_code: string) => {
  const url = `https://www.strava.com/api/v3/oauth/token?client_id=${client_id}&client_secret=${client_secret}&code=${authorization_code}&grant_type=authorization_code`;
  const response: Promise<Response> = await fetch(url, {
    method: 'POST',
  })
    .then((res) => res.json())
    .catch((error) => {
      Sentry.captureException(error);
      console.error(error.message);
    });

  return response;
};

/**
 * access_token profile: read_all_permission
 * @param access_token
 * @returns
 */
export const getZones = async (access_token: string) => {
  const url = 'https://www.strava.com/api/v3/athlete/zones';
  const zones: Promise<Zones> = await fetch(url, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })
    .then((res) => res.json())
    .catch((error) => console.error(error.message));

  return zones;
};

export const getActivityZones = async (
  activityId: number,
  access_token: string
) => {
  const url = `https://www.strava.com/api/v3/activities/${activityId}/zones`;
  const activityZones: Promise<ActivityZone[]> = await fetch(url, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })
    .then((res) => res.json())
    .catch((error) => console.error(error.message));

  return activityZones;
};

export const getGlobalStats = async (userID: string, access_token: string) => {
  const url = `https://www.strava.com/api/v3/athletes/${userID}/stats`;
  const globalStats: Promise<ActivityStats> = await fetch(url, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })
    .then((res) => res.json())
    .catch((error) => {
      Sentry.captureException(error);
      console.error(error.message);
    });

  return globalStats;
};
